import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Search,
  Edit3,
  Trash2,
  Eye,
  Mail,
  Phone,
  Building2,
  CheckCircle,
  XCircle,
  Shield,
  RefreshCw,
  Save,
  X,
  Key
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, AdminUser } from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

export const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [editForm, setEditForm] = useState<Partial<AdminUser>>({})

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadUsers()
  }, [currentUser, navigate, page, searchQuery])

  const loadUsers = async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.getUsers({
        search: searchQuery || undefined,
        page,
        limit: 20
      })
      setUsers(response.users)
      setTotalPages(response.totalPages)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      await adminApi.deleteUser(userId)
      toast.success('User deleted successfully')
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete user')
    }
  }

  const handleViewUser = async (userId: string) => {
    try {
      const user = users.find(u => u.id === userId)
      if (user) {
        setSelectedUser(user)
        const userDetails = await adminApi.getUser(userId)
        setSelectedUserDetails(userDetails)
      }
    } catch (error) {
      toast.error('Failed to load user details')
    }
  }

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user)
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      isIdentityVerified: user.isIdentityVerified,
      isKycComplete: user.isKycComplete,
      isAccreditedInvestor: user.isAccreditedInvestor
    })
  }

  const handleSaveUser = async () => {
    if (!editingUser) return

    try {
      await adminApi.updateUser(editingUser.id, editForm)
      toast.success('User updated successfully')
      setEditingUser(null)
      loadUsers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user')
    }
  }

  const handleResetNewUser = async (userId: string, userEmail: string) => {
    if (userEmail !== 'newuser@open1031.ai') {
      toast.error('This reset function is only for the newuser@open1031.ai demo account')
      return
    }

    if (!confirm(`Reset newuser demo state? This will clear their KYC verification, exchange agreement, and wizard data. They will need to go through the onboarding flow again.`)) {
      return
    }

    try {
      const result = await adminApi.resetDemoUser(userId)
      toast.success(result.message || 'Newuser demo state reset successfully')
      loadUsers()
      
      // Clear all exchange-related localStorage data for newuser
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (
          key.includes('exchange_wizard') || 
          key.includes('exchange_agreement') || 
          key.includes('wizard_state') ||
          key.includes('exchange_docs')
        )) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
      toast.info('Local storage cleared. User should refresh the page.')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset newuser demo state')
    }
  }

  const handleResetPassword = async (userId: string) => {
    if (!confirm('Reset password for this user? They will need to set a new password on next login.')) {
      return
    }

    try {
      // This would typically call an admin API endpoint to reset password
      toast.success('Password reset email sent (mock)')
    } catch (error: any) {
      toast.error('Failed to reset password')
    }
  }

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <Users className="h-8 w-8 text-primary-600" />
            User Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage all users across the platform
          </p>
        </div>
        <Button onClick={loadUsers} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search users by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Users Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Exchanges
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Accounts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-4 flex-1 min-w-0">
                        {editingUser?.id === user.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editForm.firstName || ''}
                              onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                              placeholder="First Name"
                              className="text-sm"
                            />
                            <Input
                              value={editForm.lastName || ''}
                              onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                              placeholder="Last Name"
                              className="text-sm"
                            />
                            <Input
                              value={editForm.email || ''}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              placeholder="Email"
                              type="email"
                              className="text-sm"
                            />
                            <Input
                              value={editForm.phone || ''}
                              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                              placeholder="Phone"
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {user.firstName} {user.lastName}
                              {user.isAdmin && (
                                <Shield className="h-4 w-4 inline-block ml-2 text-yellow-500" />
                              )}
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
                              <Mail className="h-3 w-3 text-slate-500 dark:text-slate-400" />
                              {user.email}
                            </div>
                            {user.phone && (
                              <div className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                <Phone className="h-3 w-3 text-slate-500 dark:text-slate-400" />
                                {user.phone}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser?.id === user.id ? (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={editForm.isIdentityVerified || false}
                            onChange={(e) => setEditForm({ ...editForm, isIdentityVerified: e.target.checked })}
                            className="rounded"
                          />
                          <span>Identity Verified</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={editForm.isKycComplete || false}
                            onChange={(e) => setEditForm({ ...editForm, isKycComplete: e.target.checked })}
                            className="rounded"
                          />
                          <span>KYC Complete</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={editForm.isAccreditedInvestor || false}
                            onChange={(e) => setEditForm({ ...editForm, isAccreditedInvestor: e.target.checked })}
                            className="rounded"
                          />
                          <span>Accredited Investor</span>
                        </label>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {user.isIdentityVerified ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            <XCircle className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                        {user.isKycComplete && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            <Shield className="h-3 w-3 mr-1" />
                            KYC Complete
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      {user.exchangeCount || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {user.accountCount || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {editingUser?.id === user.id ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSaveUser}
                            className="text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingUser(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewUser(user.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResetPassword(user.id)}
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          {user.email === 'newuser@open1031.ai' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetNewUser(user.id, user.email)}
                              title="Reset Newuser Demo State"
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {!user.isAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  User Details
                </h2>
                <Button variant="outline" onClick={() => setSelectedUser(null)}>
                  Close
                </Button>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Name</label>
                    <p className="text-slate-900 dark:text-slate-100">{selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Email</label>
                    <p className="text-slate-900 dark:text-slate-100">{selectedUser.email}</p>
                  </div>
                  {selectedUser.phone && (
                    <div>
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Phone</label>
                      <p className="text-slate-900 dark:text-slate-100">{selectedUser.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Created</label>
                    <p className="text-slate-900 dark:text-slate-100">{new Date(selectedUser.createdAt).toLocaleString()}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">Status</label>
                  <div className="flex gap-2 flex-wrap">
                    {selectedUser.isIdentityVerified ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Pending Verification
                      </span>
                    )}
                    {selectedUser.isKycComplete && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        <Shield className="h-3 w-3 mr-1" />
                        KYC Complete
                      </span>
                    )}
                    {selectedUser.isAccreditedInvestor && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        Accredited Investor
                      </span>
                    )}
                    {selectedUser.isAdmin && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        <Shield className="h-3 w-3 mr-1" />
                        Admin
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">Exchanges ({selectedUser.exchangeCount || 0})</label>
                  <div className="space-y-2">
                    {selectedUser.exchangeCount === 0 ? (
                      <p className="text-sm text-slate-500 dark:text-slate-400">No exchanges</p>
                    ) : (
                      <>
                        {selectedUserDetails?.exchanges && selectedUserDetails.exchanges.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {selectedUserDetails.exchanges.map((ex: any) => (
                              <div key={ex.id} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{ex.name}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Status: {ex.status}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(null)
                            navigate('/app/admin/exchanges')
                          }}
                        >
                          <Building2 className="h-4 w-4 mr-2" />
                          View All Exchanges
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">Accounts ({selectedUser.accountCount || 0})</label>
                  {selectedUserDetails?.accounts && selectedUserDetails.accounts.length > 0 ? (
                    <div className="space-y-2">
                      {selectedUserDetails.accounts.map((acc: any) => (
                        <div key={acc.id} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Account ID: {acc.accountId}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Role: {acc.role}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Member of {selectedUser.accountCount || 0} account(s)
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleEditUser(selectedUser)
                      setSelectedUser(null)
                    }}
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit User
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleResetPassword(selectedUser.id)}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                  {selectedUser.email === 'newuser@open1031.ai' && (
                    <Button
                      variant="outline"
                      onClick={() => handleResetNewUser(selectedUser.id, selectedUser.email)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset Newuser Demo
                    </Button>
                  )}
                  {!selectedUser.isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleDeleteUser(selectedUser.id)
                        setSelectedUser(null)
                      }}
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete User
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}


